import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getTokenHelper } from "@/helper/getTokens";
import { logout } from "@/helper/logout";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useDevice } from "@/hooks/useDevice";
import { useTenantData } from "@/hooks/useTenantData";
import { useTenantFeature } from "@/hooks/useTenantFeature";
import { getActiveResume } from "@/store/resume/selectors";
import { getTenant } from "@/store/tenant/selectors";
import { getUserDetails } from "@/store/user/selectors";
import { UsingReactChildren } from "@/types";
import { DeviceContextType } from "@/types/device";
import { AppRoutes } from "@/types/routes";
import { ITenantFeature } from "@/types/tenant";
import { getIsAdminRole } from "@/utils/getIsRole";
import {
    getIsDesktopOnlyRoute,
    getIsJobsPage,
    getIsLivePreviewPage,
    getIsPreferencesPage,
    getIsResumePage,
    getIsYourJobsPage,
} from "@/utils/routes";

export const ProtectedRoute = ({ children }: UsingReactChildren) => {
    const token = getTokenHelper("accessToken");
    const isDesktopOnlyRoute = getIsDesktopOnlyRoute();
    const { isDesktop } = useDevice() as DeviceContextType;
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const userDetails = useAppSelector(getUserDetails);
    const isResumePage = getIsResumePage();
    const activeResume = useAppSelector(getActiveResume);
    const isAdminUser = getIsAdminRole(userDetails?.role);
    const tenant = useAppSelector(getTenant);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isPreferencesPage = getIsPreferencesPage();
    const isPrefWizardEnabled = useTenantFeature(
        ITenantFeature.PreferencesWizard,
    );
    const isLivePreviewPage = getIsLivePreviewPage();
    const isJobsPage = getIsJobsPage();
    const isJobsEnabled = useTenantFeature(ITenantFeature.JOBS);
    const isYourJobsPage = getIsYourJobsPage();
    const isYourJobsEnabled = useTenantFeature(ITenantFeature.USER_JOBS);

    const { isLoading: isTenantLoading } = useTenantData();

    useEffect(() => {
        if (isDesktopOnlyRoute && !isDesktop) {
            setShouldRedirect(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Purposefully leaving empty so only runs on mount of page.

    useEffect(() => {
        // If the user is not the owner of the resume, redirect to the dashboard
        if (
            isResumePage &&
            activeResume &&
            userDetails?.id !== activeResume?.user &&
            !isAdminUser
        ) {
            setShouldRedirect(true);
        }
    }, [userDetails, activeResume, isResumePage, isAdminUser]);

    // If user is accessing a page that is not the same tenant as their user, log them out.
    // Don't do this for admins
    useEffect(() => {
        if (isAdminUser) {
            return;
        }
        if (userDetails && tenant && userDetails?.tenantId !== tenant?.id) {
            logout(dispatch, navigate);
        }
    }, [dispatch, isAdminUser, navigate, tenant, userDetails]);

    // Tenant Redirects - only run after tenant data is loaded
    useEffect(() => {
        if (isTenantLoading) return;

        if (isPreferencesPage && !isPrefWizardEnabled) {
            setShouldRedirect(true);
        }
        if (isJobsPage && !isJobsEnabled) {
            setShouldRedirect(true);
        }
        if (isYourJobsPage && !isYourJobsEnabled) {
            setShouldRedirect(true);
        }
    }, [
        isTenantLoading, // Add this dependency
        isPreferencesPage,
        isPrefWizardEnabled,
        isJobsPage,
        isJobsEnabled,
        isYourJobsPage,
        isYourJobsEnabled,
    ]);

    if (!isLivePreviewPage) {
        if (!token?.token) {
            // user is not authenticated
            return <Navigate to={`/${AppRoutes.Login}`} />;
        } else if (shouldRedirect) {
            window.location.href = "/";
            return null;
        }
    }
    return children;
};
